import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { prdDraftQueries } from '@/lib/api/queries/prd-draft'
import {
  useUpdatePrdDraft,
  useRejectPrdDraft,
  usePublishPrdDraft,
  useUnpublishPrdDraft,
} from '@/lib/api/mutations/prd-draft'
import { prdEditSchema, type PrdEditFormData } from '@/lib/constants/draft'
import { PrdDraftStickyHeader } from './prd-draft-sticky-header'
import { DraftBodyViewer } from './draft-body-viewer'
import { DraftBodyEditor } from './draft-body-editor'
import { PrdDraftSidebar } from './prd-draft-sidebar'
import { PrdExportControls } from './prd-export-controls'
import { DraftDetailSkeleton } from './draft-detail-skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface PrdDraftDetailPageProps {
  agentId: string
  draftId: string
}

export function PrdDraftDetailPage({ agentId, draftId }: PrdDraftDetailPageProps) {
  // Dedicated PRD endpoint — separate from blog drafts.
  const { data: draft, isLoading, isError } = useQuery(prdDraftQueries.detail(agentId, draftId))

  // State (discard dialog, edit mode, reject dialog).
  const [isEditing, setIsEditing] = useState(false)
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)

  // Mutations. Hooks must run before the early returns below — React rules of hooks.
  const updateMutation = useUpdatePrdDraft(agentId, draftId)
  const rejectMutation = useRejectPrdDraft(agentId, draftId)
  const publishMutation = usePublishPrdDraft(agentId, draftId)
  const unpublishMutation = useUnpublishPrdDraft(agentId, draftId)

  // RHF form — body-only slice per D-01 / D-02.
  const form = useForm<PrdEditFormData>({
    resolver: zodResolver(prdEditSchema),
    defaultValues: { body: draft?.body ?? '' },
  })

  // RHF uses a proxy for formState — fields must be accessed during render to
  // be tracked reactively. Access isDirty so the discard-dialog gate (D-07)
  // sees the latest value when handleDiscard runs.
  const isFormDirty = form.formState.isDirty

  if (isLoading) return <DraftDetailSkeleton />

  // Transient fetch error (network / 5xx): retry is meaningful, so show the
  // "try again" copy. Kept identical to the Phase 26 shipped split.
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <p className="text-destructive mb-4">
          Unable to load this draft. Please go back and try again.
        </p>
      </div>
    )
  }

  // Query settled successfully but no draft returned (404 / empty seed).
  // This is terminal — retry won't help — so surface "not found" instead of a
  // misleading generic retry message.
  if (!draft) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <p className="text-destructive mb-4">Draft not found.</p>
      </div>
    )
  }

  // Handlers

  // D-01: narrow form — just body. Reset before entering edit mode so the
  // textarea default matches the currently-saved body.
  const handleStartEdit = () => {
    form.reset({ body: draft.body })
    setIsEditing(true)
  }

  // D-04: body PATCH via invalidate-only hook. No revert-on-published branch.
  const handleSave = () => {
    form.handleSubmit((data) => {
      updateMutation.mutate(
        { body: data.body },
        { onSuccess: () => setIsEditing(false) },
      )
    })()
  }

  // D-07: AlertDialog-gated discard — quiet when clean, confirm when dirty.
  // Use the render-time isFormDirty value so the subscription (declared above)
  // keeps this in sync with RHF's proxy.
  const handleDiscard = () => {
    if (isFormDirty) {
      setShowDiscardDialog(true)
      return
    }
    form.reset()
    setIsEditing(false)
  }

  const confirmDiscard = () => {
    form.reset()
    setIsEditing(false)
    setShowDiscardDialog(false)
  }

  // PRD reject is reason-less for now — prd_drafts has no rejection_reason
  // column. Use a simple confirm rather than the blog RejectDialog, which
  // would otherwise ask the user to type a reason that we'd silently drop.
  const handleConfirmReject = () => {
    rejectMutation.mutate(undefined, {
      onSuccess: () => setShowRejectDialog(false),
    })
  }

  // View-mode action set:
  //   draft   → Publish (primary), Edit, Reject
  //   published → Edit, Unpublish (sends back to drafts)
  //   exported → Edit, Publish (export and publish are orthogonal, so a
  //              user can still promote an exported draft into Published)
  //   rejected → empty (no redraft path yet)
  const viewActions = (
    <>
      {(draft.status === 'draft' || draft.status === 'exported') && (
        <>
          <Button
            onClick={() => publishMutation.mutate()}
            disabled={publishMutation.isPending}
          >
            {publishMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Publish
          </Button>
          <Button onClick={handleStartEdit} variant="outline">
            Edit Draft
          </Button>
          {draft.status === 'draft' && (
            <Button
              variant="outline"
              className="text-destructive"
              onClick={() => setShowRejectDialog(true)}
            >
              Reject Draft
            </Button>
          )}
        </>
      )}
      {draft.status === 'published' && (
        <>
          <Button onClick={handleStartEdit}>Edit</Button>
          <Button
            variant="outline"
            onClick={() => unpublishMutation.mutate()}
            disabled={unpublishMutation.isPending}
          >
            {unpublishMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Unpublish
          </Button>
        </>
      )}
    </>
  )

  // D-11 edit-mode action set — no share affordance, no Approve/Schedule shortcuts.
  const editActions = (
    <>
      <Button onClick={handleSave} disabled={updateMutation.isPending}>
        {updateMutation.isPending && (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        )}
        Save Changes
      </Button>
      <Button
        variant="outline"
        onClick={handleDiscard}
        disabled={updateMutation.isPending}
      >
        Discard Changes
      </Button>
    </>
  )

  return (
    <div className="h-full flex flex-col">
      <PrdDraftStickyHeader
        draft={draft}
        agentId={agentId}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        viewActions={viewActions}
        editActions={editActions}
      />

      {/* D-14: rejection reason callout — inlined verbatim from blog analog. */}
      {draft.status === 'rejected' && draft.rejectionReason && (
        <div className="bg-destructive/5 border border-destructive/20 rounded-lg mx-4 md:mx-8 mt-4 p-4">
          <p className="text-sm font-medium text-destructive mb-1">Rejection reason</p>
          <p className="text-sm text-muted-foreground">{draft.rejectionReason}</p>
        </div>
      )}

      <div className="flex-1 overflow-auto p-4 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            {isEditing ? (
              // D-05: reuse DraftBodyEditor generic, omit the image toolbar prop so the
              // conditional toolbar row stays hidden. Explicit generic binds the form
              // type to PrdEditFormData; no casts.
              <DraftBodyEditor<PrdEditFormData>
                register={form.register}
                watch={form.watch}
                error={form.formState.errors.body?.message}
              />
            ) : (
              <DraftBodyViewer body={draft.body} enableHeadingIds showLogo />
            )}
          </div>
          <div className="md:col-span-1 flex flex-col gap-4">
            {/* D-06: live-update stats + sections summary from the form buffer
                 during edit; fall back to the saved state otherwise. */}
            <PrdDraftSidebar
              draft={draft}
              bodyOverride={isEditing ? form.watch('body') : undefined}
            />
            <PrdExportControls agentId={agentId} draft={draft} />
          </div>
        </div>
      </div>

      {/* Simple confirm — no reason field while prd_drafts has no rejection_reason column. */}
      <AlertDialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject this PRD?</AlertDialogTitle>
            <AlertDialogDescription>
              The PRD will be marked as rejected. You can edit it and resubmit
              later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={rejectMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmReject}
              disabled={rejectMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {rejectMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* D-07: shadcn AlertDialog for dirty-discard confirmation. */}
      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Discard them?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscard}>Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
